define('viewModels/privileged/composer/EntitySubviewMixinProvider',[
    'lib/knockout',
    'lib/lodash4',
    'services/toastService'
], function(
    ko,
    _,
    toastService
) {
    "use strict";

    /*
     *  EntitySubviewMixinProvider is used to provide some standard functionality for managing a list of subview entites; it provides general methods for reordering and deleting those entities
     *  It's mostly intended to be used with

     * Arguments:
         viewModel - the viewModel that the functions will be attached to
         entityName- the capitalized string name of the entity, for use in function names. e.g. "QuestionGroup"
            note: a localized string must exist for any entity with a delete method, and the key must match the entity name (ex. composer.evaluations.editor.questionGroup)
         entityModelList - an observable array that has the models that represent each entity
         (optional) options - an object potentially containing the following properties:
             modifiedState -  A function (e.g. an observable) called with true when the entityList is modified

     * Usage example: (where "self" is a viewmodel)
         self.entityList = vmUtils.subviewList(self, model.entities, EntityViewModel)
         var entitySubviewMixinProvider = new EntitySubviewMixinProvider(self, "EntityList", model.entities)

         entitySubviewMixinProvider.addReorderMethods();
         //self.moveEntityList, self.triggerMoveEntityUp, self.triggerMoveEntityDown are now defined

         entitySubviewMixinProvider.addDeleteMethods();
         //self.deleteEntity, self.triggerDeleteEntity are now defined

     * Notes :
         -The delete code relies on the entity subviewList defined in vmUtils.js,
            which expects that each model will have a modelId property that uniquely identifies it; though this could be generalized if need be.

         -The handlers prefixed with "trigger", e.g. "triggerMoveEntityUp" have knockout event handler signatures of data, evt and use $index to determine the entities index

     */

    var UNDO_DELETE_TOPIC_ROOT = "composer.undo.";

    var EntitySubviewMixinProvider = function (viewModel, entityName, entityModelList, entityViewList, options) {
        var mixin = this;

        options = options || {};

        //Property names for attaching methods to the viewmodel based on entityName

        // Deletion methods
        var DELETE_FN_PROPERTY = "delete"+entityName; //deleteEntity
        var TRIGGER_DELETE_FN_PROPERTY = "triggerDelete"+entityName; //triggerDeleteEntity

        // Reorder methods
        var MOVE_FN_PROPERTY = "move"+entityName; //moveEntity
        var TRIGGER_MOVE_UP_FN_PROPERTY   = "triggerMove"+entityName+"Up";   //triggerMoveEntityUp
        var TRIGGER_MOVE_DOWN_FN_PROPERTY = "triggerMove"+entityName+"Down"; //triggerMoveEntityDown

        //Period of time in which a deleted model can be replaced
        var REPLACE_TIMEOUT = 60000;

        options = _.defaults(options, {
            modifiedState: _.noop
        });

        mixin.addReorderMethods = function() {
            viewModel[MOVE_FN_PROPERTY] = function (start, end) {
                var length = entityModelList().length;
                if(start < 0 || end < 0 || start >= length || end >= length || start === end) {
                    return;
                }
                var moving = entityModelList.splice(start, 1)[0]; //remove 1 element at position start
                entityModelList.splice(end,0,moving); //remove 0 elements at position end, and add the removed element
                options.modifiedState(true);
            };

            viewModel[TRIGGER_MOVE_UP_FN_PROPERTY] = function (data, evt) {
                var index = -1;

                if (!evt) {
                    // if there is no event, then see if the data is an entity and get its index
                    for(var i = 0; i < entityModelList().length; i++) {
                        var curEntity = entityModelList()[i];
                        if (curEntity.modelId === data.modelId) {
                            index = i;
                        }
                    }
                } else {
                    // Otherwise, just get the index from the event context
                    index = ko.contextFor(evt.target).$index();
                }

                if (data.validateCanMoveUp(entityViewList)) {
                    viewModel[MOVE_FN_PROPERTY](index, index-1);
                }
            };

            viewModel[TRIGGER_MOVE_DOWN_FN_PROPERTY] = function (data, evt) {
                var index = -1;

                if (!evt) {
                    // if there is no event, then see if the data is an entity and get its index
                    for(var i = 0; i < entityModelList().length; i++) {
                        var curEntity = entityModelList()[i];
                        if (curEntity.modelId === data.modelId) {
                            index = i;
                        }
                    }
                } else {
                    // Otherwise, just get the index from the event context
                    index = ko.contextFor(evt.target).$index();
                }

                if (data.validateCanMoveDown(entityViewList)) {
                    viewModel[MOVE_FN_PROPERTY](index, index+1);
                }
            };
        };

        mixin.addDeleteMethods = function() {

            // Add Delete
            viewModel[DELETE_FN_PROPERTY] = function(index) {

                //Removes the entity from the modelList
                var entityModel = entityModelList.splice(index, 1)[0];
                var modelId = entityModel.modelId;
                //Remove from viewModels list
                var entityViewModel = entityViewList.removeById(modelId);
                //Clean up the subview
                viewModel.disposeSubView(entityViewModel);
                options.modifiedState(true); //Mark the evaluation as modified

                // Select the next, or the previous, or the parent (in that order of preference)
                if(options.handleSelection) {
                    var sibling = entityModelList()[index] || entityModelList()[index-1];
                    if(sibling) {
                        // sibling (next or prev)
                        var siblingViewModel = entityViewList.getById(sibling.modelId);
                        siblingViewModel.select();
                    } else if(viewModel.select) {
                        // parent
                        viewModel.select();
                    }
                }

                // Display the undo alert
                var entityNameCopy = entityName.charAt(0).toLowerCase() + entityName.slice(1);
                var lEntityName = ko.i18n.renderString('composer.entity.' + entityNameCopy);

                var topicName = UNDO_DELETE_TOPIC_ROOT + modelId;

                toastService.notifyMessage("composer.delete.notification.title", {
                    icon: "fa-check-circle",
                    bodyMessageKey: "composer.delete.notification.body",
                    messageData: {
                        type: lEntityName
                    },
                    publishOnUserInteraction: {
                        topic: topicName,
                        data: {}
                    }
                });

                function replaceModel() {
                    // Put the saved object back into the modelList
                    entityModelList.splice(index, 0, entityModel);

                    // Select it
                    var entityViewModel = entityViewList.getById(modelId);
                    if (entityViewModel) {
                        if(options.handleSelection) {
                            entityViewModel.select();
                        }
                        options.modifiedState(true);
                    }

                    //Remove callback from toast service
                    replaceModel_dispose();
                }

                function replaceModel_dispose() {
                    toastService.unsubscribeFromUserInteraction(topicName, replaceModel);
                }

                toastService.subscribeToUserInteraction(topicName, replaceModel);

                setTimeout(replaceModel_dispose, REPLACE_TIMEOUT);

            };

            viewModel[TRIGGER_DELETE_FN_PROPERTY] = function (data, evt) {
                var index = -1;

                if (!evt) {
                    // if there is no event, then see if the data is an entity and get its index
                    for(var i = 0; i < entityModelList().length; i++) {
                        var curEntity = entityModelList()[i];
                        if (curEntity.modelId === data.modelId) {
                            index = i;
                        }
                    }
                } else {
                    // Otherwise, just get the index from the event context
                    index = ko.contextFor(evt.target).$index();
                }

                if (index >= 0) {
                    viewModel[DELETE_FN_PROPERTY](index);
                }
            };
        };

    };

    return EntitySubviewMixinProvider;
});
